import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DateRangePicker,
    displayFormatTime,
    Enums,
    FormStore,
    InfiniteScroll,
    inject,
    NothingHere,
    SubTopBar,
    TasksStore,
    toKeyArray,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsBarChart } from '@react-icons/all-files/bs/BsBarChart';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownItem, DropdownMenu, DropdownToggle, Form, UncontrolledButtonDropdown } from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { DisplaysStore } from '../DisplaysStore';

export interface ITelemetryListViewParams {
    serialNumber: string;
}
export interface ITelemetryListViewProps extends RouteComponentProps<ITelemetryListViewParams> {}

@autoinject
@observer
export class TelemetryListView extends React.Component<ITelemetryListViewProps> {
    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.match.params.serialNumber);
    }

    @computed
    private get hasMore() {
        return this.scrollCount < this.telemetry.length;
    }
    public constructor(props: ITelemetryListViewProps) {
        super(props);

        const startTime = this.formStore.fields.startTime.value;
        startTime.setMonth(startTime.getMonth() - 1);

        const endTime = this.formStore.fields.endTime.value;
        endTime.setDate(endTime.getDate() + 1);
        endTime.setMinutes(59);
        endTime.setSeconds(59);
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getTelemetryAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            {this.telemetry?.length ? '' : 'No Results'}

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>

                            <a className="btn btn-link" onClick={this.exportCsvUrlAsync}>
                                Export to CSV
                            </a>
                        </div>
                    </Form>

                    <UncontrolledButtonDropdown className="ms-auto">
                        <DropdownToggle caret size="lg" color="link">
                            <BsListUl />
                        </DropdownToggle>

                        <DropdownMenu end>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/telemetry/chart`}>
                                <DropdownItem>
                                    <BsBarChart /> Performance Chart
                                </DropdownItem>
                            </LinkContainer>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/telemetry/table`}>
                                <DropdownItem>
                                    <BsListUl className="" /> Performance List
                                </DropdownItem>
                            </LinkContainer>
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                </SubTopBar>

                <InfiniteScroll
                    throttle={100}
                    threshold={300}
                    hasMore={this.hasMore}
                    onLoadMore={this.loadMore}
                    container={this.appStore.mainContainer}
                >
                    <table className="table table-striped fixed-header">
                        <thead>
                            <tr>
                                <th>Status Message</th>
                                <th>Poll Time</th>
                                {toKeyArray(Enums.MeasurementId).map(key => (
                                    <th key={key} style={{ minWidth: '125px' }}>
                                        {Enums.toString(Enums.MeasurementId, key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {this.telemetry.slice(0, this.scrollCount).map((p, idx) => (
                                <tr key={idx}>
                                    <td
                                        className={
                                            p.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical
                                                ? 'text-danger'
                                                : ''
                                        }
                                    >
                                        {Enums.toString(Enums.DisplayStatus, p.displayStatus)},{' '}
                                        {Enums.toString(Enums.DisplayHealth, p.displayHealth)}
                                    </td>
                                    <td>
                                        {displayFormatTime(
                                            p.pollTime,
                                            'EEEE, MMMM do yyyy, h:mm:ss a',
                                            this.props.match.params.serialNumber
                                        )}
                                    </td>

                                    {toKeyArray(Enums.MeasurementId).map(key => (
                                        <td key={key}>
                                            {this.measurementValue(p.measurements.find(s => s.id == key))}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </InfiniteScroll>

                <NothingHere
                    empty={!this.telemetry.length}
                    icon={<BsCardHeading />}
                    label="No Data Available"
                    loading={this.loading}
                    loadingMessage="Loading Data"
                />
            </div>
        );
    }

    @bind
    private measurementValue(measurement?: Mri.WebApi.Measurement) {
        let value;
        if (measurement) {
            const { boolValue, intValue, floatValue, stringValue, durationValue, units = '' } = measurement;
            value = boolValue ?? intValue ?? floatValue ?? stringValue ?? durationValue?.toString();
            if (value && units === '%' && typeof value === 'number') value *= 100;
            value += units;
        } else value = '-';
        return value;
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getTelemetryAsync();
    }

    @action
    private async getTelemetryAsync() {
        this.loading = true;
        try {
            const telemetry = await this.displaysStore.getTelemetryLogAsync(
                this.props.match.params.serialNumber,
                this.formStore.values.startTime,
                this.formStore.values.endTime
            );

            if (telemetry) this.telemetry = telemetry;
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async exportCsvUrlAsync() {
        const query = stringify({
            startTime: this.formStore.values.startTime,
            endTime: this.formStore.values.endTime,
        });
        const task = this.tasksStore.startSessionTask('Export Telemetry Poll');

        try {
            await this.displaysStore.exportTelemetryLogAsync(this.props.match.params.serialNumber, query);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    @action
    private loadMore() {
        this.scrollCount += 40;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private scrollCount = 40;
    @observable
    private loading = false;
    @observable
    private telemetry: Mri.TelemetryPolls.TelemetryPoll[] = [];
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
